import selectors from './selectors';
import argumentDataSources from '@/schema/argumentDataSources';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import credentialScreenSelectors from '@/screens/apps/redux/selectors/credentialScreenSelectors.js';
import { createSelector } from '@/redux/lib/createSelector.js';
import { netsuiteSublists } from '@shared/schema/netsuiteSublists.js';
import { toTitleCase } from '@shared/utils/toTitleCase.js';
import { netsuiteSubrecords } from '@shared/schema/netsuiteSubrecords.js';

const dataTypeMap = {
  boolean: 'boolean',
  date: 'date',
  datetime: 'date',
  double: 'number',
  integer: 'number',
  long: 'number',
  float: 'number',
  currency: 'number',
  currency2: 'number',
  string: 'string',
  text: 'string',
  unknown: 'string',
  select: 'string',
  textarea: 'string'
};

const activeAppCredentialById = createSelector(
  'arg-activeAppCredentialById',
  selectors.credentials,
  (_, credentialId) => credentialId,
  (_, __, appId) => appId,
  (creds, credentialId, appId) => creds.find((n) => n.appId === appId && n.credentialId === credentialId)
);
const activeWorkflowStepCredentialId = createSelector('arg-activeWorkflowStepCredentialId', workflowScreenSelectors.activeWorkflowStep, (step) => step?.credentialId);
// Embedded steps bind an integration app instead of a credential; their internal actions run with
// the editor's test installation connection, so both ids travel with the request.
const activeWorkflowStepIntegrationAppId = createSelector('arg-activeWorkflowStepIntegrationAppId', workflowScreenSelectors.activeWorkflowStep, (step) => step?.integrationAppId);
const appDefaultTestCredentialId = createSelector('arg-appDefaultTestCredentialId', appScreenSelectors.activeAppDefaultTestCred, (credential) => credential?.credentialId);
const workflowVariableActiveArguments = createSelector('arg-workflowVariableActiveArguments', workflowScreenSelectors.activeWorkflowStepOptions, (options) => options?.arguments);

const homeActiveCredential = createSelector(
  'homeActiveCredential',
  [selectors.credentials, (_) => selectors.query(_, 'credentialId')], //
  (creds, credentialId) => creds?.find((n) => n?.credentialId === credentialId)
);
const homeActiveApp = createSelector(
  'homeActiveApp',
  [selectors.appsVersions, homeActiveCredential], //
  (apps, activeCredential) => apps?.find((n) => n?.id === activeCredential?.appId)
);
const homeActiveAuth = createSelector(
  'homeActiveAuth',
  [homeActiveApp, homeActiveCredential], //
  (app, credential) => app?.authentication?.find((n) => n?.type === credential?.meta?.type || n?.type === credential?.secrets?.type)
);

const getNetsuiteArgumentDefinitions = (action, args, netsuiteRecordSchema) => {
  const isCreate = action?.operation === 'create';
  const recordType = action?.arguments?.find((n) => n.key === 'recordType')?.defaultValue || args?.recordType?.value;
  const recordSchema = netsuiteRecordSchema?.[recordType]?.schema;
  const recordCatalog = netsuiteRecordSchema?.[recordType]?.catalog || [];

  const netsuiteArgumentDefinitions = (() => {
    const result = [];

    if (args?.columns?.value?.length > 0) {
      const filteredColumns = args?.columns?.value?.filter((column) => column !== 'id');
      filteredColumns?.forEach((column) => {
        const schemaItem = recordSchema?.fields?.find((n) => n?.id?.toLowerCase() === column?.toLowerCase());
        const catalogItem = recordCatalog?.find((n) => n?.id?.toLowerCase() === column?.toLowerCase());
        const mappedDataType = dataTypeMap[catalogItem?.dataType?.toLowerCase()] || dataTypeMap[schemaItem?.dataType?.toLowerCase()] || 'string';

        result.push({
          id: column,
          key: column,
          name: catalogItem?.label || schemaItem?.label || column,
          dataType: mappedDataType,
          location: 'body',
          required: schemaItem?.isRequired || false,
          hidden: false,
          custom: !schemaItem && !catalogItem
        });
      });
    }

    // Add all required fields that weren't already included
    if (recordSchema?.fields && isCreate) {
      recordSchema.fields.forEach((schemaItem) => {
        if (schemaItem?.isRequired && schemaItem?.id !== 'id') {
          const alreadyIncluded = result.some((item) => item.id?.toLowerCase() === schemaItem.id?.toLowerCase());
          if (!alreadyIncluded) {
            const catalogItem = recordCatalog?.find((n) => n?.id?.toLowerCase() === schemaItem.id?.toLowerCase());
            const mappedDataType = dataTypeMap[catalogItem?.dataType?.toLowerCase()] || dataTypeMap[schemaItem?.dataType?.toLowerCase()] || 'string';

            result.push({
              id: schemaItem.id,
              key: schemaItem.id,
              name: catalogItem?.label || schemaItem?.label || schemaItem.id,
              dataType: mappedDataType,
              location: 'body',
              required: true,
              hidden: false,
              custom: !catalogItem
            });
          }
        }
      });
    }

    // Process sublists
    if (args?.sublists?.value?.length > 0) {
      result.push({
        id: 'sublists',
        key: 'sublists',
        name: 'Sublists',
        dataType: 'object',
        location: 'body',
        required: false,
        hidden: false
      });

      args?.sublists?.value?.forEach((sublist, index) => {
        result.push({
          id: `sublists${index}`,
          key: `sublists.${sublist?.sublistName}[]`,
          name: netsuiteSublists[sublist?.sublistName] || toTitleCase(sublist?.sublistName),
          dataType: 'array',
          location: 'body',
          required: false,
          hidden: false
        });

        sublist?.fields?.forEach((field) => {
          const sublistItem = recordSchema?.sublists?.[sublist?.sublistName]?.find((n) => n?.id?.toLowerCase() === field?.toLowerCase());
          const mappedDataType = dataTypeMap[sublistItem?.dataType?.toLowerCase()] || 'string';
          result.push({
            id: `sublists${index}${field}`,
            key: `sublists.${sublist?.sublistName}[].${field}`,
            name: sublistItem?.label || field,
            dataType: mappedDataType,
            location: 'body',
            required: sublistItem?.isRequired || false,
            hidden: false,
            custom: !sublistItem
          });
        });
      });
    }

    // Process subrecords
    if (args?.subrecords?.value?.length > 0) {
      result.push({
        id: 'subrecords',
        key: 'subrecords',
        name: 'Subrecords',
        dataType: 'object',
        location: 'body',
        required: false,
        hidden: false
      });

      args?.subrecords?.value?.forEach((subrecord, index) => {
        result.push({
          id: `subrecords${index}`,
          key: `subrecords.${subrecord?.subrecordName}`,
          name: netsuiteSubrecords[subrecord?.subrecordName] || toTitleCase(subrecord?.subrecordName),
          dataType: 'object',
          location: 'body',
          required: false,
          hidden: false
        });

        subrecord?.fields?.forEach((field) => {
          const subrecordItem = recordSchema?.subrecords?.[subrecord?.subrecordName]?.find((n) => n?.id?.toLowerCase() === field?.toLowerCase());
          const mappedDataType = dataTypeMap[subrecordItem?.dataType?.toLowerCase()] || 'string';
          result.push({
            id: `subrecords${index}${field}`,
            key: `subrecords.${subrecord?.subrecordName}.${field}`,
            name: subrecordItem?.label || field,
            dataType: mappedDataType,
            location: 'body',
            required: subrecordItem?.isRequired || false,
            hidden: false,
            custom: !subrecordItem
          });
        });
      });
    }

    return result;
  })();
  return { ...action, arguments: netsuiteArgumentDefinitions };
};

const activeWorkflowStepCustomArgumentsAppAction = createSelector(
  'activeWorkflowStepCustomArgumentsAppAction', //
  [workflowScreenSelectors.activeWorkflowStepAppAction, workflowScreenSelectors.activeWorkflowStepArguments, (state) => state.workflow.netsuiteRecordSchema],
  getNetsuiteArgumentDefinitions
);

const appActionCustomArgumentsAppAction = createSelector(
  'appActionCustomArgumentsAppAction', //
  [appScreenSelectors.activeAppAction, appScreenSelectors.activeAppActionInstanceArguments, (state) => state.workflow.netsuiteRecordSchema],
  getNetsuiteArgumentDefinitions
);

const activeWorkflowStepCustomArgumentsArguments = createSelector(
  'activeWorkflowStepCustomArgumentsArguments', //
  [workflowScreenSelectors.activeWorkflowStepOptions], //
  (options) => options?.customArguments
);

const appActionCustomArgumentsArguments = createSelector(
  'appActionCustomArgumentsArguments', //
  [appScreenSelectors.activeAppActionInstanceOptions], //
  (options) => options?.customArguments
);

const activeWorkflowStepCustomArgumentsArgument = createSelector(
  'activeWorkflowStepCustomArgumentsArgument',
  activeWorkflowStepCustomArgumentsArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);

const appActionCustomArgumentsArgument = createSelector(
  'appActionCustomArgumentsArgument',
  appActionCustomArgumentsArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);

const argumentSelectors = {
  [argumentDataSources.workflowStep]: {
    activeApp: workflowScreenSelectors.activeWorkflowStepApp,
    activeAppApi: workflowScreenSelectors.activeWorkflowStepAppAPI,
    activeAuth: workflowScreenSelectors.activeWorkflowStepActiveAuth,
    activeAction: () => workflowScreenSelectors.activeWorkflowStepAppAction,
    activeArguments: () => workflowScreenSelectors.activeWorkflowStepArguments,
    activeOptions: workflowScreenSelectors.activeWorkflowStepOptions,
    activeArgumentValue: workflowScreenSelectors.activeWorkflowStepArgument,
    activeCredential: activeAppCredentialById,
    activeCredentialId: activeWorkflowStepCredentialId,
    activeIntegrationAppId: activeWorkflowStepIntegrationAppId,
    activeInstallationId: workflowScreenSelectors.activeWorkflowTestInstallationId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [`${argumentDataSources.workflowStep}-customArguments`]: {
    activeApp: workflowScreenSelectors.activeWorkflowStepApp,
    activeAppApi: workflowScreenSelectors.activeWorkflowStepAppAPI,
    activeAuth: workflowScreenSelectors.activeWorkflowStepActiveAuth,
    activeAction: () => activeWorkflowStepCustomArgumentsAppAction,
    activeArguments: () => activeWorkflowStepCustomArgumentsArguments,
    activeOptions: workflowScreenSelectors.activeWorkflowStepOptions,
    activeArgumentValue: activeWorkflowStepCustomArgumentsArgument,
    activeCredential: activeAppCredentialById,
    activeCredentialId: activeWorkflowStepCredentialId,
    activeIntegrationAppId: activeWorkflowStepIntegrationAppId,
    activeInstallationId: workflowScreenSelectors.activeWorkflowTestInstallationId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.credentialEditPage]: {
    activeApp: credentialScreenSelectors.activeCredentialApp,
    activeAppApi: undefined,
    activeAuth: credentialScreenSelectors.activeCredentialAppAuth,
    activeAction: undefined,
    activeArguments: undefined,
    activeOptions: undefined,
    activeArgumentValue: undefined,
    activeCredential: activeAppCredentialById,
    activeCredentialId: credentialScreenSelectors.activeCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appAuthenticationInstance]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: undefined,
    activeAuth: appScreenSelectors.activeAppAuth,
    activeAction: undefined,
    activeArguments: undefined,
    activeOptions: undefined,
    activeArgumentValue: undefined,
    activeCredential: activeAppCredentialById,
    activeCredentialId: undefined,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionInstance]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ overrideAction } = {}) =>
      overrideAction
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: ({ lookupActionId, overrideAction } = {}) =>
      overrideAction && lookupActionId
        ? appScreenSelectors.activeAppActionInstanceArgumentsById //
        : appScreenSelectors.activeAppActionInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [`${argumentDataSources.appActionInstance}-customArguments`]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: () => appActionCustomArgumentsAppAction,
    activeArguments: () => appActionCustomArgumentsArguments,
    activeOptions: appScreenSelectors.activeAppActionInstanceOptions,
    activeArgumentValue: appActionCustomArgumentsArgument,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appLongPollingActionInstance]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ overrideAction } = {}) =>
      overrideAction
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionLongPollingInstance,
    activeOptions: appScreenSelectors.activeAppActionLongPollingInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionLongPollingInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appLongPollingExtraActionInstance]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ overrideAction } = {}) =>
      overrideAction
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppExtraActionLongPollingInstance,
    activeOptions: appScreenSelectors.activeAppExtraActionLongPollingInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppExtraActionLongPollingInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionCustomArguments]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: undefined,
    activeAuth: undefined,
    activeAction: ({ lookupActionId } = {}) =>
      lookupActionId
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: ({ lookupActionId, overrideAction } = {}) =>
      overrideAction && lookupActionId
        ? appScreenSelectors.activeAppActionInstanceArgumentsById //
        : appScreenSelectors.activeAppActionInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionFeaturesCustomArgumentsByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionArgumentList]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ lookupActionId } = {}) =>
      lookupActionId
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionListInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionListInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionListInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionArgumentListTest]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ lookupActionId } = {}) =>
      lookupActionId
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionListTestInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionListTestInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionListTestInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionFilters]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: undefined,
    activeAuth: undefined,
    activeAction: ({ lookupActionId } = {}) =>
      lookupActionId
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionFeaturesFiltersInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionFeaturesFiltersInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionFeaturesFiltersInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionSort]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: undefined,
    activeAuth: undefined,
    activeAction: ({ lookupActionId } = {}) =>
      lookupActionId
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionFeaturesFiltersInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionFeaturesFiltersInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionFeaturesFiltersInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.appActionOnCompletionHookActionInstance]: {
    activeApp: appScreenSelectors.activeApp,
    activeAppApi: appScreenSelectors.activeAppApi,
    activeAuth: undefined,
    activeAction: ({ overrideAction } = {}) =>
      overrideAction
        ? appScreenSelectors.activeAppActionById //
        : appScreenSelectors.activeAppAction,
    activeArguments: () => appScreenSelectors.activeAppActionOnCompletionHookInstanceArguments,
    activeOptions: appScreenSelectors.activeAppActionOnCompletionHookInstanceOptions,
    activeArgumentValue: appScreenSelectors.activeAppActionOnCompletionHookInstanceArgumentByKeyAndIndex,
    activeCredential: undefined,
    activeCredentialId: appDefaultTestCredentialId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.workflowVariables]: {
    activeApp: workflowScreenSelectors.activeWorkflowStepApp,
    activeAppApi: workflowScreenSelectors.activeWorkflowStepAppAPI,
    activeAuth: workflowScreenSelectors.activeWorkflowStepActiveAuth,
    activeAction: () => workflowScreenSelectors.activeWorkflowStepAppAction,
    activeArguments: () => workflowVariableActiveArguments,
    activeOptions: workflowScreenSelectors.activeWorkflowStepOptions,
    activeArgumentValue: workflowScreenSelectors.activeWorkflowStepOptionsArgument,
    activeCredential: activeAppCredentialById,
    activeCredentialId: activeWorkflowStepCredentialId,
    activeIntegrationAppId: activeWorkflowStepIntegrationAppId,
    activeInstallationId: workflowScreenSelectors.activeWorkflowTestInstallationId,
    activeCredentials: workflowScreenSelectors.activeWorkflowStepCredentials
  },
  [argumentDataSources.home]: {
    activeApp: homeActiveApp,
    activeCredential: homeActiveCredential,
    activeCredentialId: (_) => selectors.query(_, 'credentialId'),
    activeCredentials: selectors.credentials,
    activeAuth: homeActiveAuth
  }
};

export default argumentSelectors;
